/**
 * DisputeSendEmailModal
 *
 * Thin wrapper around SingleEmailModal that provides dispute-specific
 * props (source type, linked transaction IDs for invoice attachment fetching).
 *
 * Fetches linked transaction IDs internally via the dispute detail endpoint
 * rather than relying on page-level data (which may not include transactions).
 *
 * Follows the exact same pattern as TransactionSendCopyInvoiceModal.
 */

import { useCallback, useMemo } from "react";
import {
  SingleEmailModal,
  type EmailAttachment,
} from "@/components/shared/single-email-modal";
import { useTransactionDocumentDownload } from "@/hooks/useTransactionDocumentDownload";
import { useDisputeByNumericId } from "@/hooks/useDisputesData";
import { useLogDisputeEmail } from "@/hooks/useLogDisputeEmail";
import { toast } from "sonner";

export interface DisputeSendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disputeId: number;
  customerId: number;
  customerName?: string;
  accountNumber?: string;
  onSuccess?: () => void;
}

export function DisputeSendEmailModal({
  open,
  onOpenChange,
  disputeId,
  customerId,
  customerName = "",
  accountNumber = "",
  onSuccess,
}: Readonly<DisputeSendEmailModalProps>) {
  const { downloadDocumentAsBase64 } = useTransactionDocumentDownload();
  const logDisputeEmail = useLogDisputeEmail();

  const customerIdStr = String(customerId);

  const { data: disputeDetail } = useDisputeByNumericId(
    open ? disputeId : undefined,
  );

  const linkedTransactionIds = useMemo(
    () =>
      (disputeDetail?.transactions ?? [])
        .map((t) => t.transactionId)
        .filter((id) => id > 0),
    [disputeDetail],
  );

  const handleFetchAttachments = useCallback(
    async (
      onProgress: (done: number, total: number) => void,
    ): Promise<EmailAttachment[]> => {
      if (linkedTransactionIds.length === 0) return [];

      onProgress(0, linkedTransactionIds.length);

      const results = await Promise.allSettled(
        linkedTransactionIds.map(async (txId, idx) => {
          const doc = await downloadDocumentAsBase64(txId);
          onProgress(idx + 1, linkedTransactionIds.length);
          return doc;
        }),
      );

      const successful = results
        .filter(
          (r): r is PromiseFulfilledResult<EmailAttachment> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value);

      const failedCount = results.filter(
        (r) => r.status === "rejected",
      ).length;
      if (failedCount > 0) {
        toast.warning(
          `${failedCount} invoice document(s) could not be downloaded`,
        );
      }

      return successful;
    },
    [linkedTransactionIds, downloadDocumentAsBase64],
  );

  const handleAfterSend = useCallback(
    (payload: {
      toEmails: string[];
      ccEmails: string[];
      subject: string;
      htmlBody: string;
      templateId?: string;
      templateName?: string;
      isSubjectModified: boolean;
      isBodyModified: boolean;
      attachOutstandingInvoices: boolean;
    }) => {
      logDisputeEmail.mutate(
        {
          sourceId: String(disputeId),
          toRecipients: payload.toEmails,
          ccRecipients: payload.ccEmails.length > 0 ? payload.ccEmails : undefined,
          subject: payload.subject,
          htmlBody: payload.htmlBody,
          templateId: payload.templateId ? parseInt(payload.templateId, 10) : undefined,
          templateName: payload.templateName,
          isSubjectModified: payload.isSubjectModified,
          isBodyModified: payload.isBodyModified,
          attachOutstandingInvoices: payload.attachOutstandingInvoices,
        },
        {
          onError: () => {
            toast.error("Email was sent but failed to save to history. Please contact support if the issue persists.");
          },
        },
      );
    },
    [logDisputeEmail, disputeId],
  );

  return (
    <SingleEmailModal
      open={open}
      onOpenChange={onOpenChange}
      title="Send Email"
      customerId={customerIdStr}
      customerName={customerName}
      accountNumber={accountNumber}
      sourceType="dispute"
      sourceId={disputeId}
      transactionIds={linkedTransactionIds}
      callbackEventName="creditcontrol:dispute-email-done"
      attachLabel="Attach outstanding invoices"
      fetchAttachments={handleFetchAttachments}
      onAfterSend={handleAfterSend}
      onSuccess={onSuccess}
      allowedCategoryTypes={["disputes", "general"]}
      allowedTemplateTypes={["email"]}
    />
  );
}
